import { StatusBadge } from "@components";
import { FileViewOnlyCard } from "@components/FileViewOnlyCard";
import { useBlackList } from "@hooks/procurements/vendors/useBlackLists";
import { IconPlus, IconEdit01 } from "@pentabd/icons";
import { Button, SectionHeader, Table, Text } from "@pentabd/ui";
import { UploadFileType } from "@type/HRM/upload-file";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HistoryModal from "./HistoryModal";
import { FormProvider, useForm } from "react-hook-form";
import { useSingleBlackList } from "@hooks/procurements/vendors/useSingleBlackList";
import { revertMultipleDocumentData } from "@utils/documentDataConvert";
import isUpdateButtonShownHandler from "@utils/isUpdateButtonShown";

interface COLUMNSType {
  t: any;
  findFirstVendorHistory: (id: number | string) => number | undefined;
  openHistoryModal: (id: number | null) => void;
  getData: any;
}

const COLUMNS = ({
  t,
  findFirstVendorHistory,
  openHistoryModal,
  getData,
}: COLUMNSType) => [
  {
    id: 1,
    name: t("PROCUREMENT_ADD_VENDORS.FORM.TABLE.FROM_DATE"),
    key: "fromDate",
    render: (fromDate: string) => {
      return <Text className="text-subtitle2 text-nowrap">{fromDate}</Text>;
    },
  },
  {
    id: 2,
    name: t("PROCUREMENT_ADD_VENDORS.FORM.TABLE.TO_DATE"),
    key: "toDate",
    render: (toDate: string) => {
      return <Text className="text-subtitle2 text-nowrap">{toDate}</Text>;
    },
  },
  {
    id: 3,
    name: t("PROCUREMENT_ADD_VENDORS.FORM.TABLE.BLACK_LISTED"),
    key: "isBlackList",
    render: (isBlackList: string) => {
      return (
        <StatusBadge
          customStatus={!isBlackList ? "success" : "danger"}
          status={isBlackList ? "Blacklisted" : "WhiteListed"}
        />
      );
    },
  },
  {
    id: 4,
    name: t("PROCUREMENT_ADD_VENDORS.FORM.TABLE.DOCUMENTS"),
    key: "historyDocuments",
    render: (historyDocuments: UploadFileType[]) => (
      <FileViewOnlyCard data={historyDocuments ? historyDocuments : []} />
    ),
  },

  {
    id: 5,
    name: "",
    key: "isBlackList",
    render: (isBlackList: string, row: any) => {
      const isShown = isUpdateButtonShownHandler(row?.id, getData);

      return isShown && isBlackList ? (
        <IconEdit01
          size={24}
          fill="subtitle1"
          onClick={() => openHistoryModal(row?.id)}
        />
      ) : (
        <></>
      );
    },
  },
];

export default function HistoryTable({ id }: { id: number | string }) {
  const { t } = useTranslation();
  const { getData } = useBlackList(id);

  const [openModal, setOpenModal] = useState(false);
  const [historyId, setHistoryId] = useState<number | null>(null);

  const { getSingleData } = useSingleBlackList(id, historyId);

  const method = useForm({
    values: historyId
      ? (revertMultipleDocumentData(getSingleData, ["historyDocuments"]) as any)
      : {},
  });

  const closeModal = () => {
    setOpenModal(false);
    setHistoryId(null);
  };

  const findFirstVendorHistory = (id: number | string) => {
    let isFirst;
    getData.forEach((history: any, idx: number) => {
      if (history.id === id) {
        isFirst = idx;
      }
    });
    return isFirst;
  };

  const openHistoryModal = (historyId: number | null) => {
    setOpenModal(true);
    setHistoryId(historyId);
  };

  useEffect(() => {
    const reset = () => {
      setOpenModal(false);
      setHistoryId(null);
    };

    reset();
  }, []);

  console.log("get single data", getSingleData);

  return (
    <div className="pt-10">
      <FormProvider {...method}>
        {/* modal */}
        <HistoryModal
          openModal={openModal}
          id={id}
          historyId={historyId}
          closeModal={closeModal}
        />
      </FormProvider>
      {/* modal */}
      <SectionHeader title={t("PROCUREMENT_ADD_VENDORS.FORM.TABLE.HEADER")} />

      <div className="pt-10">
        <Table
          header={{
            buttons: [
              !getData[0]?.isBlackList ? (
                <Button
                  fill="fill"
                  type="primary"
                  size="sm"
                  onClick={() => setOpenModal(true)}
                >
                  <IconPlus size="16" fill="white" />{" "}
                  {t(
                    "PROCUREMENT_ADD_VENDORS.FORM.TABLE.ADD_HISTORY_BUTTON_TEXT",
                  )}
                </Button>
              ) : (
                <></>
              ),
            ],
          }}
          columns={COLUMNS({
            t,
            findFirstVendorHistory,
            openHistoryModal,
            getData,
          })}
          rows={getData || []}
        />
      </div>
    </div>
  );
}
